/**
 * Account-level value lists shared by the API (`users.status`, `users.use_case`
 * and `otp_codes.purpose` columns and their validators), the wire contract
 * (`@kiao-voice/api-contract/auth`) and the web staff roster filters.
 *
 * These columns carry a DB CHECK; the migrations keep their own frozen copies,
 * and `apps/api/tests/unit/value_list_check_constraints.spec.ts` asserts the live
 * CHECK set equals these constants.
 */

/**
 * `unverified` marks a lead captured by the anonymous-trial email capture; it
 * never appears in self-service auth payloads, only in the staff roster.
 */
export const USER_STATUSES = ['active', 'deactivated', 'unverified'] as const
export type UserStatus = (typeof USER_STATUSES)[number]

/**
 * The subset of `UserStatus` staff may SET directly on the roster
 * (`PATCH .../status`): `unverified` is never a staff-chosen target, only a
 * state a lead starts in.
 */
export const MANAGED_USER_SETTABLE_STATUSES = ['active', 'deactivated'] as const
export type ManagedUserSettableStatus = (typeof MANAGED_USER_SETTABLE_STATUSES)[number]

/**
 * 422 codes of `PUT /internal/users/:id/voice-prompt-label`: a label Langfuse
 * would not accept, or the cap on how many users may carry an override.
 */
export const VOICE_PROMPT_LABEL_ERRORS = ['invalid_label', 'too_many_overrides'] as const
export type VoicePromptLabelError = (typeof VOICE_PROMPT_LABEL_ERRORS)[number]

export const OTP_PURPOSES = ['signup', 'login'] as const
export type OtpPurpose = (typeof OTP_PURPOSES)[number]

/**
 * 新用戶在網頁版逐字稿列表回答的「最想先用 KIAO Voice 做什麼」（`users.use_case`）。
 * 沒回答或跳過時欄位是 NULL；跳過與沒回答用 `users.use_case_answered_at` 區分。
 * email 條件的值域是這組再加 `unknown`（`EMAIL_RULE_USE_CASES`）。
 */
export const USER_USE_CASES = ['meeting', 'lecture', 'media', 'dictation', 'other'] as const
export type UserUseCase = (typeof USER_USE_CASES)[number]

/**
 * 錯誤碼：這個動作要求帳號目前的信箱已由 OTP、magic link 或寄到信箱的邀請連結證明過，
 * 而這個帳號還沒有。以 HTTP 403 `{ error }` 回應；client 收到後引導使用者對自己的信箱
 * 完成驗證（`POST /auth/email-verification/request`＋`/verify`），再重試原本的動作。
 */
export const EMAIL_VERIFICATION_REQUIRED_ERROR = 'email_verification_required'
export type EmailVerificationRequiredErrorCode = typeof EMAIL_VERIFICATION_REQUIRED_ERROR
